import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ErroApi } from '../api/client'
import { modeloComArquivo as api, type SituacaoDoEnvio, type StatusDoEnvio } from '../api/modeloComArquivo'
import { INTERVALO_DA_CONSULTA_MS, LIMITE_DA_CONSULTA_MS, type EnvioAcompanhado } from './envioAcompanhado'

/**
 * O estado de entrega do último modelo enviado nesta conversa: enviada, entregue, lida ou
 * falhou.
 *
 * 🔴 **O caso (09/10):** a aba dizia só que o Chatwoot aceitou. A recusa da Meta chega
 * depois — de 107 modelos enviados, 3 falharam — e quem enviou só a via se voltasse à aba
 * Mensagens. Aqui a linha se atualiza sozinha, e a falha aparece em vermelho, com o motivo.
 *
 * ⭐ **A consulta tem três freios, e nenhum é opcional:**
 * 1. para no estado final (`lida` ou `falhou`);
 * 2. para 10 minutos depois do envio — quem não leu até lá pode levar horas;
 * 3. **não consulta com a aba escondida** — nem com a aba do navegador em segundo plano,
 *    nem com a aba "Enviar arquivo" fora da tela dentro do Chatwoot (ver `abaNaTela`). É o resíduo da 282
 *    (o painel pedia o contexto a cada 2 s mesmo escondido), que aqui não se repete.
 */
const SITUACOES: SituacaoDoEnvio[] = ['enviada', 'entregue', 'lida', 'falhou']

const ehFinal = (s: SituacaoDoEnvio) => s === 'lida' || s === 'falhou'

/**
 * Como se sabe que a aba está na tela — **duas perguntas, e as duas têm de dizer sim**:
 *
 * 1. `visibilityState`: a aba do NAVEGADOR em segundo plano.
 * 2. `IntersectionObserver` no documento: o painel escondido DENTRO do Chatwoot, que esconde
 *    o dashboard app com `v-show` (`display: none` no pai do iframe).
 *
 * 🔴 **O tamanho da janela não serve** (revisão do PR #81, medido em Chromium com iframe de
 * verdade): o iframe em `display: none` continua com `innerWidth × innerHeight` de antes e
 * `visibilityState = "visible"`. A primeira versão testava o tamanho, e consultava escondida.
 * O observador avisa `false` ao esconder e `true` ao voltar.
 *
 * ⚠️ Até o observador falar pela primeira vez, a resposta é "não sei" — e não se consulta.
 * Ele fala no primeiro quadro desenhado; num iframe que nasce escondido, não fala, e é o certo.
 */
const temObservador = typeof IntersectionObserver !== 'undefined'

export function StatusDoEnvioDoModelo({ conversa, envio }: { conversa: number; envio: EnvioAcompanhado }) {
  const { t } = useTranslation()

  const [status, setStatus] = useState<StatusDoEnvio | null>(null)
  const [desistiu, setDesistiu] = useState(false)

  useEffect(() => {
    let vivo = true
    let acabou = false
    let consultando = false
    let ultima = 0

    // Navegador sem o observador: vale só a pergunta 1.
    let desenhada: boolean | null = temObservador ? null : true

    const abaNaTela = () => document.visibilityState === 'visible' && desenhada === true

    const observador = temObservador
      ? new IntersectionObserver((entradas) => {
          desenhada = entradas[entradas.length - 1]?.isIntersecting ?? false
          aoVoltar()
        })
      : null

    const limite = envio.em + LIMITE_DA_CONSULTA_MS

    function parar() {
      acabou = true
      clearInterval(relogio)
      document.removeEventListener('visibilitychange', aoVoltar)
      observador?.disconnect()
    }

    async function consultar() {
      if (!vivo || acabou || consultando) return

      if (Date.now() >= limite) {
        parar()
        setDesistiu(true)
        return
      }

      // Escondida: nada de rede. O relógio segue, e a volta à tela consulta na hora.
      if (!abaNaTela()) return

      consultando = true
      ultima = Date.now()

      try {
        const lido = await api.status(conversa, envio.mensagemId)

        if (!vivo) return

        // Resposta que não é o estado (proxy, versão antiga do servidor): fica o que havia.
        if (!lido || !SITUACOES.includes(lido.situacao)) return

        setStatus(lido)

        if (ehFinal(lido.situacao)) parar()
      } catch (e) {
        // 401, 403 e 404 não mudam esperando: a sessão caiu, ou a mensagem não existe mais.
        // O resto (rede, 502 do Chatwoot fora) é passageiro — a próxima volta tenta de novo.
        if (vivo && e instanceof ErroApi && e.status >= 400 && e.status < 500) {
          parar()
          setDesistiu(true)
        }
      } finally {
        consultando = false
      }
    }

    function aoVoltar() {
      if (abaNaTela() && Date.now() - ultima >= INTERVALO_DA_CONSULTA_MS) void consultar()
    }

    const relogio = setInterval(() => void consultar(), INTERVALO_DA_CONSULTA_MS)

    document.addEventListener('visibilitychange', aoVoltar)
    observador?.observe(document.documentElement)

    // Com o observador, a primeira consulta sai quando ele disser que a aba está na tela.
    void consultar()

    return () => {
      vivo = false
      parar()
    }
  }, [conversa, envio.mensagemId, envio.em])

  const situacao = status?.situacao

  // A hora do envio vai na linha: dois envios do mesmo modelo não se confundem, e o estado de
  // um envio anterior não é lido como o do que acabou de ser recusado.
  const de = {
    nome: envio.nome,
    hora: new Date(envio.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
  }

  if (situacao === 'falhou')
    return (
      <p className="avaliacao__erro enviar-modelo__status" role="alert" data-testid="status-do-envio" data-situacao="falhou">
        {status?.motivo
          ? t('modeloComArquivo.status.falhouComMotivo', { ...de, motivo: status.motivo })
          : t('modeloComArquivo.status.falhou', de)}
      </p>
    )

  return (
    <p
      className="avaliacao__dica enviar-modelo__status"
      role="status"
      data-testid="status-do-envio"
      data-situacao={situacao ?? 'consultando'}
    >
      {t(`modeloComArquivo.status.${situacao ?? (desistiu ? 'semEstado' : 'consultando')}`, de)}
      {desistiu && situacao && situacao !== 'lida' && <> {t('modeloComArquivo.status.parou')}</>}
    </p>
  )
}
